/**
 * The assessment photo is cropped to 3:4 by `createFaceCropFromImage`.
 * The zone cards use an `aspect-[2/1]` container with `object-fit: cover`.
 *
 * For `object-fit: cover` on a portrait image in a landscape container the
 * image is scaled to fill the container width; the surplus image height that
 * overflows top/bottom is:
 *   overflow_fraction = (container_aspect / image_aspect) - 1
 *
 * `object-position Y%` scrolls that overflow so that `Y%` of the overflow
 * sits above the visible window.  To center landmark `centerY_norm` (0–1)
 * inside the container:
 *
 *   Y% = ( centerY_norm × ratio – 0.5 ) / ( ratio – 1 ) × 100
 *   where ratio = container_aspect / image_aspect
 */
export const CONTAINER_ASPECT = 2.0; // aspect-[2/1]
export const IMAGE_ASPECT = 3 / 4; // createFaceCropFromImage target

/**
 * Converts normalized MediaPipe landmark coordinates to a CSS
 * `object-position` string that centers a facial zone in a 2:1 card holding
 * a 3:4 portrait photo with `object-fit: cover`.
 *
 * @param centerY  Normalized y-coordinate (0–1) of the zone's midpoint.
 * @param centerX  Normalized x-coordinate (0–1) of the zone's midpoint.
 */
export function computeObjectPosition(centerY: number, centerX: number): string {
  const ratio = CONTAINER_ASPECT / IMAGE_ASPECT; // ≈ 2.667
  const yPct = ((centerY * ratio - 0.5) / (ratio - 1)) * 100;
  const xPct = centerX * 100;
  return `${xPct.toFixed(1)}% ${Math.max(0, Math.min(100, yPct)).toFixed(1)}%`;
}

/** Used while detection is in-flight or if detection fails. */
export const FACIAL_THIRDS_FALLBACK = {
  upper: "center 12%",
  middle: "center 40%",
  lower: "center 68%",
} as const;
